import { useEffect } from "react";
import { Alert, Button, Flex, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";

import { COMPANY_ROLE_ENUM, ItemInfoType } from "interfaces";
import { formatWithDecimals, parseErrorMessage } from "utils";
import { ErrorCard, WhiteBoxBlock } from "ui";
import { ItemInfo, ItemInfoBlock } from "components";
import { useStores } from "stores";
import { UpdateAdditionalFeesModal, UpdateFeeModal, UpdateRateModal } from "./modals";

import { DoubleActionsSettingsBlock } from "./DoubleActionsSettingsBlock";
import { ADDITIONAL_FEES } from "utils/envConstants";
import { useNavigate } from "react-router-dom";
import { routes } from "constants/routes";
import { AdditionalFeesBlock } from "./AdditionalFeesBlock";
import styles from "./settingsPage.module.css";

export const SettingsPage = observer(() => {
  const { settings, actionModals, user } = useStores();
  const navigate = useNavigate();
  const {
    isSystemAdmin,
    isSeniorCustomer,
    isJuniorCustomer,
    isSeniorLP,
    isJuniorLP,
    isLPRiskManager,
    isJuniorOperator,
    isSeniorOperator,
  } = user.roles;

  useEffect(() => {
    if (isSystemAdmin) {
      settings.fetchCompanyFees();
      settings.fetchRates();
      if (ADDITIONAL_FEES) {
        settings.fetchRequestFeeSettings();
      }
    }
  }, [isSystemAdmin, settings]);

  const { customerBaseFeeImp, customerBaseFeeExp, lpBaseFeeImp, lpBaseFeeExp } = settings.companyFees;
  const { rates, requestFeeSettings } = settings;

  const displayDoubleActionsSettings =
    isSeniorCustomer || isJuniorCustomer || isSeniorLP || isJuniorLP || isLPRiskManager;

  const showEditFeeBtn = (ADDITIONAL_FEES && isSystemAdmin) || !ADDITIONAL_FEES;

  const providerFeeItems = requestFeeSettings.find((item) => item.role === COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER);
  const customerFeeItems = requestFeeSettings.find((item) => item.role === COMPANY_ROLE_ENUM.CUSTOMER);

  const openFeesModal = () => {
    actionModals.open(
      (modalProps: any) => {
        return ADDITIONAL_FEES ? (
          <UpdateAdditionalFeesModal config={modalProps.config} />
        ) : (
          <UpdateFeeModal config={modalProps.config} />
        );
      },
      {
        width: ADDITIONAL_FEES ? "1300px" : "600px",

        noValidation: true,
        title: "Редактирование комиссии сделки",
        isOverlayClose: false,
        onApply: () => {
          return Promise.resolve();
        },
      }
    );
  };

  const openRatesModal = () => {
    actionModals.open(() => <UpdateRateModal rates={rates} />, {
      width: "600px",
      isOverlayClose: false,
      noValidation: true,
      title: "Редактирование курса на день",
      onApply: () => {
        return Promise.resolve();
      },
    });
  };

  const feesItems: ItemInfoType[] = [
    {
      label: (
        <Flex vertical>
          <Typography.Text className="margin-bottom-xs" strong>
            Комиссии УВЭД
          </Typography.Text>{" "}
          <Typography.Text className="text-description text-xs">
            {"Комиссия за экспортную сделку (УВЭД)"}
          </Typography.Text>
        </Flex>
      ),
      text: customerBaseFeeExp !== null ? `${formatWithDecimals(customerBaseFeeExp * 100)} %` : "—",
    },
    {
      label: (
        <Flex vertical>
          <Typography.Text className="margin-bottom-xs" strong>
            Комиссии ПЛ
          </Typography.Text>{" "}
          <Typography.Text className="text-description text-xs">{"Комиссия за экспортную сделку (ПЛ)"}</Typography.Text>
        </Flex>
      ),
      text: lpBaseFeeExp !== null ? `${formatWithDecimals(lpBaseFeeExp * 100)} %` : "—",
    },
    {
      label: "Комиссия за импортную сделку (УВЭД)",
      text: customerBaseFeeImp !== null ? `${formatWithDecimals(customerBaseFeeImp * 100)} %` : "—",
    },

    {
      label: "Комиссия за импортную сделку (ПЛ)",
      text: lpBaseFeeImp !== null ? `${formatWithDecimals(lpBaseFeeImp * 100)} %` : "—",
    },
  ].filter((item) => !!item);

  const ratesItems: ItemInfoType[] = [
    ...Object.keys(rates.rates).map((rate) => ({
      label: `Курс ${rate}`,
      text: `${formatWithDecimals(rates.rates[rate])} руб.`,
    })),
  ].filter((item) => !!item);

  const feeBlockTitle = (
    <Flex justify="space-between" align="center">
      <Typography.Text className="whitebox-title">Комиссии платформы</Typography.Text>
      {showEditFeeBtn && (
        <Button size="small" onClick={openFeesModal}>
          Редактировать
        </Button>
      )}
    </Flex>
  );

  const rateBlockTitle = (
    <Flex justify="space-between" align="center">
      <Typography.Text className="whitebox-title">Курс на день</Typography.Text>
      <Button size="small" onClick={openRatesModal}>
        Редактировать
      </Button>
    </Flex>
  );

  const feesContent = ADDITIONAL_FEES ? (
    <Flex vertical gap="middle">
      {feeBlockTitle}
      <AdditionalFeesBlock />
    </Flex>
  ) : (
    <ItemInfoBlock blockTitle={feeBlockTitle} items={feesItems} />
  );

  return (
    <Spin spinning={settings.isPending}>
      <Flex vertical gap="large">
        {settings.fetchStatus === "success" && isSystemAdmin && (
          <div className={`${styles.settingsBlock} ${ADDITIONAL_FEES ? styles.settingsBlockDirection : ""}`}>
            <div style={{ flex: 1, width: ADDITIONAL_FEES ? "40%" : "100%" }}>
              <WhiteBoxBlock>
                <ItemInfoBlock blockTitle={rateBlockTitle} items={ratesItems} />
              </WhiteBoxBlock>
            </div>

            <div style={{ flex: 1 }}>
              <WhiteBoxBlock>{feesContent}</WhiteBoxBlock>
            </div>
          </div>
        )}

        <Flex gap="large" wrap>
          <div style={{ flex: 1 }}>
            {displayDoubleActionsSettings && (
              <WhiteBoxBlock>
                <Flex vertical gap="large">
                  <DoubleActionsSettingsBlock />
                </Flex>
              </WhiteBoxBlock>
            )}
          </div>
        </Flex>
      </Flex>

      {settings.fetchStatus === "error" && settings.fetchError && (
        <ErrorCard
          status={settings.fetchError.status}
          description={settings.fetchError}
          buttonText="Вернуться на главную страницу"
          fn={() => navigate(routes.base)}
        />
      )}
    </Spin>
  );
});
